import { useCallback, useEffect, useRef, useState } from 'react';
import { cx } from '@/styled-system/css';
import type { Photo } from 'app/types/commonTypes.type';
import { buildResponsiveImageAttrs } from 'app/shared/utils/fedPhotos';
import { buildListingPhotoAlt } from 'app/shared/utils/listingUtils';
import { DesktopGalleryButtons } from './DesktopGalleryButtons';
import { ImageCounter } from './ImageCounter';
import { MobileGalleryButtons } from './MobileGalleryButtons';
import * as styles from './photoGallery.styles';
import { VerifiedBadge } from './VerifiedBadge';

type PhotoGalleryProps = {
  photos: Array<Photo>;
  displayName?: string;
  onAllPhotosClick: () => void;
  onGalleryTap?: () => void;
  initialIndex?: number;
  has3dTour?: boolean;
  hasVideos?: boolean;
  hasStreetView?: boolean;
  on3dTourClick?: () => void;
  onVideosClick?: () => void;
  onStreetViewClick?: () => void;
  trusted?: boolean;
};

const getBentoGridModifier = (photoCount: number): string => {
  switch (photoCount) {
    case 1:
      return styles.desktopBentoGrid1;
    case 2:
      return styles.desktopBentoGrid2;
    case 3:
      return styles.desktopBentoGrid3;
    default:
      return styles.desktopBentoGrid4;
  }
};

export const PhotoGallery = ({
  photos,
  displayName,
  onAllPhotosClick,
  onGalleryTap,
  initialIndex = 0,
  has3dTour,
  hasVideos,
  hasStreetView,
  on3dTourClick,
  onVideosClick,
  onStreetViewClick,
  trusted,
}: PhotoGalleryProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);
  const didScrollRef = useRef(false);
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Scroll to initialIndex on mount
  useEffect(() => {
    const track = trackRef.current;
    if (!track || initialIndex === 0) return;

    requestAnimationFrame(() => {
      isScrollingRef.current = true;
      track.scrollTo({ left: initialIndex * track.clientWidth, behavior: 'instant' });
      requestAnimationFrame(() => {
        isScrollingRef.current = false;
      });
    });
  }, [initialIndex]);

  // Sync currentIndex from scroll position
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    let rafId: number;
    const handleScroll = () => {
      didScrollRef.current = true;
      clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = setTimeout(() => {
        didScrollRef.current = false;
      }, 300);

      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        if (isScrollingRef.current) return;
        const index = Math.round(track.scrollLeft / track.clientWidth);
        setCurrentIndex(index);
      });
    };

    track.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      track.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId);
      clearTimeout(scrollTimerRef.current);
    };
  }, []);

  const scrollToIndex = useCallback(
    (index: number) => {
      const track = trackRef.current;
      if (!track) return;
      const clamped = Math.max(0, Math.min(index, photos.length - 1));
      isScrollingRef.current = true;
      track.scrollTo({ left: clamped * track.clientWidth, behavior: 'instant' });
      setCurrentIndex(clamped);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          isScrollingRef.current = false;
        });
      });
    },
    [photos.length],
  );

  const goToNext = useCallback(() => {
    scrollToIndex(currentIndex + 1);
  }, [currentIndex, scrollToIndex]);

  const goToPrev = useCallback(() => {
    scrollToIndex(currentIndex - 1);
  }, [currentIndex, scrollToIndex]);

  const handleCarouselClick = useCallback(() => {
    if (!didScrollRef.current) {
      onGalleryTap?.();
    }
  }, [onGalleryTap]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrev();
      }
    },
    [goToNext, goToPrev],
  );

  if (photos.length === 0) return null;

  const bentoPhotos = photos.slice(0, 4);

  return (
    <div className={styles.galleryContainer}>
      {/* Mobile: swipe carousel */}
      <div
        ref={containerRef}
        className={styles.mobileCarouselWrapper}
        role="region"
        aria-roledescription="carousel"
        aria-label="Property photos"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onClick={handleCarouselClick}
      >
        <div ref={trackRef} className={styles.galleryTrack}>
          {photos.map((photo, i) => {
            const imageAttrs = buildResponsiveImageAttrs(photo.url, 768, [384, 768, 1152]);

            return (
              <div key={i} className={styles.gallerySlide}>
                {imageAttrs ? (
                  <img
                    src={imageAttrs.src}
                    srcSet={imageAttrs.srcSet}
                    sizes="100vw"
                    alt={buildListingPhotoAlt(displayName, photo.caption, i)}
                    className={styles.galleryImage}
                    loading={Math.abs(i - currentIndex) <= 1 ? 'eager' : 'lazy'}
                    draggable={false}
                  />
                ) : (
                  <img
                    src={photo.url}
                    alt={buildListingPhotoAlt(displayName, photo.caption, i)}
                    className={styles.galleryImage}
                    loading={Math.abs(i - currentIndex) <= 1 ? 'eager' : 'lazy'}
                    draggable={false}
                  />
                )}
              </div>
            );
          })}
        </div>

        <ImageCounter
          currentIndex={currentIndex}
          totalCount={photos.length}
          onClick={onGalleryTap ?? onAllPhotosClick}
        />
        <MobileGalleryButtons
          hasVideos={hasVideos}
          has3dTour={has3dTour}
          hasStreetView={hasStreetView}
          onVideosClick={onVideosClick}
          on3dTourClick={on3dTourClick}
          onStreetViewClick={onStreetViewClick}
        />
      </div>

      {/* Desktop: bentobox grid */}
      <div
        className={cx(styles.desktopBentoGridBase, getBentoGridModifier(bentoPhotos.length))}
        role="region"
        aria-label="Property photos"
      >
        {bentoPhotos.map((photo, i) => {
          const imageAttrs = buildResponsiveImageAttrs(photo.url, 768, [384, 768, 1152]);

          return (
            <button
              key={i}
              className={styles.bentoButton}
              onClick={onAllPhotosClick}
              aria-label={`View photo ${i + 1} - See all photos`}
            >
              {imageAttrs ? (
                <img
                  src={imageAttrs.src}
                  srcSet={imageAttrs.srcSet}
                  sizes="50vw"
                  alt={buildListingPhotoAlt(displayName, photo.caption, i)}
                  className={styles.bentoImage}
                />
              ) : (
                <img
                  src={photo.url}
                  alt={buildListingPhotoAlt(displayName, photo.caption, i)}
                  className={styles.bentoImage}
                />
              )}
            </button>
          );
        })}

        <DesktopGalleryButtons
          onAllPhotosClick={onAllPhotosClick}
          has3dTour={has3dTour}
          hasVideos={hasVideos}
          hasStreetView={hasStreetView}
          on3dTourClick={on3dTourClick}
          onVideosClick={onVideosClick}
          onStreetViewClick={onStreetViewClick}
        />
      </div>

      {trusted && <VerifiedBadge hasOtherMobileBadges={Boolean(has3dTour || hasVideos || hasStreetView)} />}
    </div>
  );
};
